/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { Layout, Menu, ConfigProvider, Typography, Avatar, Space, Tabs } from 'antd';
import { 
  Database, 
  FileText, 
  BarChart3, 
  CheckSquare, 
  Layers, 
  LayoutDashboard, 
  Table, 
  RefreshCw, 
  Activity,
  User,
  Bell,
  Settings
} from 'lucide-react';
import { motion } from 'motion/react';
import MaterializationScheme from './components/MaterializationScheme';
import DetailAccelerationConfig from './components/DetailAccelerationConfig';

const { Header, Content, Sider } = Layout;

// Top Menu Items
const topMenuItems = [
  { key: 'data-integration', label: '数据集成', icon: <Database size={16} />, disabled: true },
  { key: 'indicator-definition', label: '指标定义', icon: <FileText size={16} />, disabled: true },
  { key: 'indicator-application', label: '指标应用', icon: <BarChart3 size={16} />, disabled: true },
  { key: 'approval-center', label: '审批中心', icon: <CheckSquare size={16} />, disabled: true },
  { key: 'indicator-materialization', label: '指标物化', icon: <Layers size={16} /> },
];

// Sidebar Menu Items for "Indicator Materialization"
const materializationSidebarItems = [
  { key: 'materialization-scheme', label: '物化方案', icon: <LayoutDashboard size={16} /> },
  { key: 'materialization-table', label: '物化表', icon: <Table size={16} /> },
  { key: 'data-backfill', label: '数据回补', icon: <RefreshCw size={16} /> },
  { key: 'monitoring-dashboard', label: '监控大盘', icon: <Activity size={16} /> },
];

interface TabItem {
  key: string;
  label: string;
  closable?: boolean;
}

export default function App() {
  const [activeTopMenu, setActiveTopMenu] = useState('indicator-materialization');
  const [collapsed, setCollapsed] = useState(false);
  
  // Multi-tab state
  const [tabs, setTabs] = useState<TabItem[]>([
    { key: 'materialization-scheme', label: '物化方案', closable: false }
  ]);
  const [activeTabKey, setActiveTabKey] = useState('materialization-scheme');

  // Handle sidebar menu click
  const handleSidebarClick = (key: string) => {
    const item = materializationSidebarItems.find(i => i.key === key);
    if (!item) return;

    // Check if tab already exists
    const exists = tabs.find(t => t.key === key);
    if (!exists) {
      setTabs([...tabs, { key: item.key, label: item.label }]);
    }
    setActiveTabKey(key);
  };

  // Function to open a specific tab (can be called from child components)
  const openTab = (key: string, label: string) => {
    const exists = tabs.find(t => t.key === key);
    if (!exists) {
      setTabs([...tabs, { key, label }]);
    }
    setActiveTabKey(key);
  };

  // Handle tab change
  const onTabChange = (key: string) => {
    setActiveTabKey(key);
  };

  // Handle tab close
  const onTabEdit = (targetKey: any, action: 'add' | 'remove') => {
    if (action === 'remove') {
      const newTabs = tabs.filter(t => t.key !== targetKey);
      setTabs(newTabs);
      // If closing active tab, switch to last tab
      if (targetKey === activeTabKey && newTabs.length > 0) {
        setActiveTabKey(newTabs[newTabs.length - 1].key);
      }
    }
  };

  const renderTabContent = (key: string) => {
    const label = [...materializationSidebarItems].find(i => i.key === key)?.label || '页面内容';
    
    return (
      <motion.div
        initial={{ opacity: 0, x: 10 }}
        animate={{ opacity: 1, x: 0 }}
        className={`${key === 'detail-acceleration-config' ? '' : 'bg-white p-6 rounded-sm shadow-sm'} min-h-[calc(100vh-140px)]`}
      >
        {key === 'materialization-scheme' ? (
          <MaterializationScheme onOpenConfig={(type) => {
            if (type === 'detail') {
              openTab('detail-acceleration-config', '明细加速配置');
            }
          }} />
        ) : key === 'detail-acceleration-config' ? (
          <DetailAccelerationConfig 
            onBack={() => onTabEdit('detail-acceleration-config', 'remove')} 
            onSave={() => onTabEdit('detail-acceleration-config', 'remove')}
          />
        ) : (
          <>
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
              <div>
                <div className="text-xs text-gray-400 mb-1">
                  指标物化 / {label}
                </div>
                <h2 className="text-xl font-semibold text-gray-800">{label}</h2>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center h-[50vh] text-gray-400">
              <div className="p-8 bg-gray-50 rounded-full mb-4">
                <Layers size={48} className="text-gray-200" />
              </div>
              <p className="text-lg">页面内容待更新</p>
              <p className="text-sm">正在开发中，敬请期待...</p>
            </div>
          </>
        )}
      </motion.div>
    );
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#1677ff',
          borderRadius: 4,
          fontFamily: '"PingFang SC", "Inter", sans-serif',
        },
        components: {
          Tabs: {
            cardPadding: '8px 20px',
            horizontalItemPadding: '8px 20px',
            margin: 0,
          }
        }
      }}
    >
      <Layout className="min-h-screen">
        {/* Top Header */}
        <Header className="flex justify-between items-center z-10 shadow-md px-6">
          <div className="flex items-center flex-1 overflow-hidden">
            <div className="flex items-center mr-8 shrink-0">
              <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center mr-2">
                <BarChart3 className="text-white" size={20} />
              </div>
              <span className="text-white font-semibold text-lg tracking-tight whitespace-nowrap">指标平台V2.0指标物化</span>
            </div>
            
            <Menu
              theme="dark"
              mode="horizontal"
              overflowedIndicator={null}
              selectedKeys={[activeTopMenu]}
              items={topMenuItems.map(item => ({
                ...item,
                onClick: () => setActiveTopMenu(item.key)
              }))}
              className="flex-1 min-w-0 border-none"
            />
          </div>

          <div className="flex items-center space-x-6">
            <Space size={20} className="text-gray-400">
              <Bell size={18} className="cursor-pointer hover:text-white transition-colors" />
              <Settings size={18} className="cursor-pointer hover:text-white transition-colors" />
            </Space>
            <div className="flex items-center space-x-2 pl-4 border-l border-gray-700">
              <Avatar size="small" icon={<User size={14} />} className="bg-blue-500" />
              <span className="text-white text-sm">管理员</span>
            </div>
          </div>
        </Header>

        <Layout>
          {/* Left Sidebar */}
          <Sider 
            width={200} 
            collapsedWidth={64}
            collapsible 
            collapsed={collapsed} 
            onCollapse={(value) => setCollapsed(value)}
            className="shadow-sm"
          >
            <div className="py-4">
              <Menu
                mode="inline"
                selectedKeys={[activeTabKey]}
                items={activeTopMenu === 'indicator-materialization' ? materializationSidebarItems.map(item => ({
                  ...item,
                  onClick: () => handleSidebarClick(item.key)
                })) : []}
                className="h-full border-none"
              />
            </div>
          </Sider>

          {/* Main Content Area */}
          <Layout className="bg-[#f5f7fa]">
            <Content className="px-4 pt-2 overflow-auto">
              <Tabs
                type="editable-card"
                hideAdd
                activeKey={activeTabKey}
                onChange={onTabChange}
                onEdit={onTabEdit}
                items={tabs.map(tab => ({
                  key: tab.key,
                  label: tab.label,
                  closable: tab.closable !== false,
                  children: renderTabContent(tab.key)
                }))}
                className="custom-tabs"
              />
            </Content>
          </Layout>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
}
